import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import { CircularProgress } from '@mui/material';
import TranslationIcon from '@/public/icons/approval/translation.svg';
import useAITranslateLanguages from '@/hooks/approval/useAITranslateLanguages';
import {
  isTranslatedAtom,
  isTranslatingAtom,
  translationResultAtom,
  translationToLangAtom,
} from './atoms';
import {
  TranslationCancelButton,
  TranslationStatusBar as TranslationStatusBarRoot,
  TranslationStatusBarInner,
  TranslationStatusLabel,
} from './styles';

/**
 * "AI 번역됨 (언어명)" 상단 띠 — isTranslatedAtom true 일 때 표시.
 * 취소: 번역중이면 in-flight 중단, isTranslated 만 false 로 — from/to·결과 atom 은
 * 같은 언어 재번역 대비 유지.
 */
type TranslationStatusBarProps = {
  onCancel?: () => void; // 진행 중 번역 중단 (useTranslateApproval.cancel)
};

const TranslationStatusBar = ({ onCancel }: TranslationStatusBarProps) => {
  const { t } = useTranslation();
  const isTranslated = useAtomValue(isTranslatedAtom);
  const isTranslating = useAtomValue(isTranslatingAtom);
  const setIsTranslated = useSetAtom(isTranslatedAtom);
  const result = useAtomValue(translationResultAtom);
  const targetLang = useAtomValue(translationToLangAtom);

  const handleCancel = useCallback(() => {
    onCancel?.();
    setIsTranslated(false);
  }, [onCancel, setIsTranslated]);

  // 메뉴에서 이미 fetch 됐으면 SWR 캐시 hit — 캐시 워밍 안 됐을 때만 1회 fetch.
  const { languages } = useAITranslateLanguages(isTranslated);

  const languageDisplay = useMemo(() => {
    const code = result?.targetLanguage ?? targetLang;
    if (!code) return '';
    const found = languages.find((l) => l.code === code);
    if (found) return found.englishName;
    return code;
  }, [languages, result, targetLang]);

  if (!isTranslated && !isTranslating) return null;

  return (
    <TranslationStatusBarRoot>
      <TranslationStatusBarInner>
        <TranslationStatusLabel>
          <TranslationIcon width={16} height={16} />
          {isTranslating ? (
            <>
              {t('AI 번역중')}
              <CircularProgress size={14} color="inherit" />
            </>
          ) : (
            <>
              {t('AI 번역됨')}
              {languageDisplay ? ` (${languageDisplay})` : ''}
            </>
          )}
        </TranslationStatusLabel>
        <TranslationCancelButton
          type="button"
          aria-label={t('번역 취소')}
          onClick={handleCancel}
        >
          {t('취소')}
        </TranslationCancelButton>
      </TranslationStatusBarInner>
    </TranslationStatusBarRoot>
  );
};

export default TranslationStatusBar;
